import { c as _c } from "react/compiler-runtime";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useId, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { LotShipmentDetails } from "@/components/inventory/shipment-picker/lot-shipment-details";
import Button from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import Input from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LotRowLabel } from "@/components/ui/lot-number-select";
import { APISearchSelect, APISearchSelectContent, APISearchSelectTrigger } from "@/components/ui/select/api-search-select";
import { useDebounce } from "@/hooks/use-debounce";
import { useLocations } from "@/hooks/use-locations";
import { formatQuantity, formatUnit } from "@/lib/format-data";
import { DoseComponentFormValues, DoseFormValues } from "./dose-form-schema";
import { DoseInventoryActions } from "./dose-inventory-actions";
import { DoseLotSelectionAction, getDoseDisplayedAllocations, getDoseLotCapabilities, getDosePickerShipments, getDoseReceiptIds, getRecordedDoseLots } from "./dose-lot-picker-model";
import { DoseLotReferenceOptions, parseDoseLotReferences, useDoseLotHistory } from "./dose-lot-reference-options";
import { doseAllocationDecisionKey, doseLotCapacity } from "./dose-lot-resolution";
import { getDoseStockCoverage, inferDoseStockAdministrationQuotas, roundPhysicalQuantity, sumAdministrationCounts } from "./dose-stock-planning";
import { DoseComponentInventoryEntry } from "./use-dose-component-inventory";
import { ShipmentLot, getAvailableLots, medicationsQueryKeys } from "@/query/medications/medications";
import { useTenantStore } from "@/stores/tenant-store";
type Allocation = NonNullable<DoseComponentFormValues["lotAllocations"]>[number];
enum DoseReceiptPresentation {
  FULL = "FULL",
  QUANTITY = "QUANTITY",
  FEEDBACK = "FEEDBACK",
}

/** Search state belongs to each field; physical selections belong only to RHF. */
function DoseInlineLotSelect(t0) {
  const $ = _c(99);
  const {
    component,
    inventory,
    stockId,
    label,
    value,
    disabled,
    showDepleted,
    onShowDepletedChange,
    onSelect,
    onClear,
    onSearchChange
  } = t0;
  const tenant = useTenantStore(_temp);
  const queryClient = useQueryClient();
  const {
    allLocationNameById
  } = useLocations();
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 300);
  const medicationId = inventory.selectedMedication?.id ?? component.medicationId;
  let t1;
  if ($[0] !== component || $[1] !== inventory) {
    t1 = getDoseLotCapabilities(component, inventory);
    $[0] = component;
    $[1] = inventory;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    action
  } = t1;
  const draws = action === DoseLotSelectionAction.DRAW;
  let t2;
  if ($[3] !== component.id || $[4] !== debouncedSearch || $[5] !== inventory.locationId || $[6] !== inventory.patientId || $[7] !== inventory.providerId || $[8] !== medicationId || $[9] !== showDepleted || $[10] !== tenant) {
    t2 = [...medicationsQueryKeys.availableLots(medicationId ?? "", inventory.locationId || undefined, inventory.providerId || undefined, component.id, showDepleted, inventory.patientId || undefined), tenant, "dose-inline-lot-search", debouncedSearch];
    $[3] = component.id;
    $[4] = debouncedSearch;
    $[5] = inventory.locationId;
    $[6] = inventory.patientId;
    $[7] = inventory.providerId;
    $[8] = medicationId;
    $[9] = showDepleted;
    $[10] = tenant;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  let t3;
  if ($[12] !== component.id || $[13] !== debouncedSearch || $[14] !== inventory.locationId || $[15] !== inventory.patientId || $[16] !== inventory.providerId || $[17] !== medicationId || $[18] !== showDepleted) {
    t3 = async () => {
      const lots = await getAvailableLots(medicationId, inventory.locationId || undefined, inventory.providerId || undefined, component.id, showDepleted, inventory.patientId || undefined, debouncedSearch.trim() || undefined);
      if (lots === null) {
        throw new Error("Inventory lot search failed");
      }
      return lots;
    };
    $[12] = component.id;
    $[13] = debouncedSearch;
    $[14] = inventory.locationId;
    $[15] = inventory.patientId;
    $[16] = inventory.providerId;
    $[17] = medicationId;
    $[18] = showDepleted;
    $[19] = t3;
  } else {
    t3 = $[19];
  }
  const t4 = !!medicationId && !disabled;
  let t5;
  if ($[20] !== t2 || $[21] !== t3 || $[22] !== t4) {
    t5 = {
      queryKey: t2,
      queryFn: t3,
      enabled: t4
    };
    $[20] = t2;
    $[21] = t3;
    $[22] = t4;
    $[23] = t5;
  } else {
    t5 = $[23];
  }
  const query = useQuery(t5);
  let t6;
  if ($[24] !== component || $[25] !== inventory || $[26] !== query.data) {
    t6 = new Set(getDosePickerShipments(component, {
      ...inventory,
      shipments: query.data ?? []
    }).map(_temp2));
    $[24] = component;
    $[25] = inventory;
    $[26] = query.data;
    $[27] = t6;
  } else {
    t6 = $[27];
  }
  const eligibleIds = t6;
  let t7;
  if ($[28] !== query.data || $[29] !== query.isError) {
    t7 = query.isError ? [] : query.data ?? [];
    $[28] = query.data;
    $[29] = query.isError;
    $[30] = t7;
  } else {
    t7 = $[30];
  }
  let t8;
  if ($[31] !== draws || $[32] !== eligibleIds || $[33] !== inventory || $[34] !== showDepleted || $[35] !== stockId || $[36] !== t7 || $[37] !== value?.id) {
    let t9;
    if ($[39] !== draws || $[40] !== eligibleIds || $[41] !== inventory || $[42] !== showDepleted || $[43] !== stockId || $[44] !== value?.id) {
      t9 = lot_0 => (!stockId || lot_0.stockId === stockId) && (showDepleted || lot_0.remainingQuantity > 0 || value?.id === lot_0.id) && (!draws || eligibleIds.has(lot_0.id) && (doseLotCapacity(lot_0, inventory).capacity > 0 || doseLotCapacity(lot_0, inventory).allowExtraYield));
      $[39] = draws;
      $[40] = eligibleIds;
      $[41] = inventory;
      $[42] = showDepleted;
      $[43] = stockId;
      $[44] = value?.id;
      $[45] = t9;
    } else {
      t9 = $[45];
    }
    t8 = t7.filter(t9);
    $[31] = draws;
    $[32] = eligibleIds;
    $[33] = inventory;
    $[34] = showDepleted;
    $[35] = stockId;
    $[36] = t7;
    $[37] = value?.id;
    $[38] = t8;
  } else {
    t8 = $[38];
  }
  const candidates = t8;
  let t9;
  if ($[46] !== allLocationNameById || $[47] !== component.name) {
    t9 = lot_1 => ({
      id: lot_1.id,
      name: lot_1.lotNumber || "Unlabeled lot",
      optionNode: <LotShipmentDetails shipment={lot_1} productName={component.name} locationNames={allLocationNameById} />
    });
    $[46] = allLocationNameById;
    $[47] = component.name;
    $[48] = t9;
  } else {
    t9 = $[48];
  }
  const formatOption = t9;
  let t10;
  if ($[49] !== component.id || $[50] !== inventory.locationId || $[51] !== inventory.patientId || $[52] !== inventory.providerId || $[53] !== medicationId || $[54] !== queryClient || $[55] !== tenant) {
    t10 = lot_2 => {
      if (!medicationId || useTenantStore.getState().currentTenant !== tenant) {
        return false;
      }
      for (const includeDepleted of [false, true]) {
        const key = medicationsQueryKeys.availableLots(medicationId, inventory.locationId || undefined, inventory.providerId || undefined, component.id, includeDepleted, inventory.patientId || undefined);
        queryClient.setQueryData(key, previous => {
          if (includeDepleted && !previous) {
            return previous;
          }
          return [...(previous ?? []).filter(entry => entry.id !== lot_2.id), lot_2];
        });
      }
      return true;
    };
    $[49] = component.id;
    $[50] = inventory.locationId;
    $[51] = inventory.patientId;
    $[52] = inventory.providerId;
    $[53] = medicationId;
    $[54] = queryClient;
    $[55] = tenant;
    $[56] = t10;
  } else {
    t10 = $[56];
  }
  const pinReceipt = t10;
  const t11 = label === "Lot number" ? "Lot Number" : label;
  let t12;
  if ($[57] !== t11) {
    t12 = <Label>{t11}</Label>;
    $[57] = t11;
    $[58] = t12;
  } else {
    t12 = $[58];
  }
  let t13;
  if ($[59] !== onClear || $[60] !== onSearchChange || $[61] !== onSelect || $[62] !== pinReceipt) {
    t13 = lot_3 => {
      if (lot_3) {
        if (pinReceipt(lot_3)) {
          onSelect(lot_3);
        }
      } else {
        onClear();
      }
      setSearch("");
      onSearchChange("");
    };
    $[59] = onClear;
    $[60] = onSearchChange;
    $[61] = onSelect;
    $[62] = pinReceipt;
    $[63] = t13;
  } else {
    t13 = $[63];
  }
  let t14;
  if ($[64] !== onSearchChange) {
    t14 = term => {
      setSearch(term);
      onSearchChange(term);
    };
    $[64] = onSearchChange;
    $[65] = t14;
  } else {
    t14 = $[65];
  }
  const t15 = query.isFetching || search !== debouncedSearch;
  let t16;
  if ($[66] !== candidates || $[67] !== t14 || $[68] !== t15) {
    t16 = {
      results: candidates,
      onSearchTermChange: t14,
      isLoading: t15
    };
    $[66] = candidates;
    $[67] = t14;
    $[68] = t15;
    $[69] = t16;
  } else {
    t16 = $[69];
  }
  let t17;
  if ($[70] !== disabled || $[71] !== label) {
    t17 = <APISearchSelectTrigger ariaLabel={label} placeholder="Search lot number, SKU, or supplier" disabled={disabled} openOnFocus={true} />;
    $[70] = disabled;
    $[71] = label;
    $[72] = t17;
  } else {
    t17 = $[72];
  }
  const t18 = query.isError ? "Inventory lots could not be loaded." : undefined;
  let t19;
  if ($[73] !== onShowDepletedChange) {
    t19 = checked => onShowDepletedChange(checked === true);
    $[73] = onShowDepletedChange;
    $[74] = t19;
  } else {
    t19 = $[74];
  }
  let t20;
  if ($[75] !== showDepleted || $[76] !== t19) {
    t20 = <Checkbox checked={showDepleted} onCheckedChange={t19} />;
    $[75] = showDepleted;
    $[76] = t19;
    $[77] = t20;
  } else {
    t20 = $[77];
  }
  let t21;
  if ($[78] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <span>Show depleted lots</span>;
    $[78] = t21;
  } else {
    t21 = $[78];
  }
  let t22;
  if ($[79] !== t20) {
    t22 = <label className="flex items-center gap-2 text-caption">{t20}{t21}</label>;
    $[79] = t20;
    $[80] = t22;
  } else {
    t22 = $[80];
  }
  let t23;
  if ($[81] !== query) {
    t23 = query.isError && <div role="alert" className="flex items-center justify-between gap-2 text-caption text-status-warning-text"><span>Could not search inventory lots. Try again.</span><Button type="button" variant="ghost" size="sm" disabled={query.isFetching} onClick={() => void query.refetch()}>Retry</Button></div>;
    $[81] = query;
    $[82] = t23;
  } else {
    t23 = $[82];
  }
  let t24;
  if ($[83] !== t22 || $[84] !== t23) {
    t24 = <div className="space-y-2">{t22}{t23}</div>;
    $[83] = t22;
    $[84] = t23;
    $[85] = t24;
  } else {
    t24 = $[85];
  }
  let t25;
  if ($[86] !== t18 || $[87] !== t24) {
    t25 = <APISearchSelectContent optionTestIdPrefix="dose-lot-choice" emptyMessage={t18} footer={t24} />;
    $[86] = t18;
    $[87] = t24;
    $[88] = t25;
  } else {
    t25 = $[88];
  }
  let t26;
  if ($[89] !== formatOption || $[90] !== t13 || $[91] !== t16 || $[92] !== t17 || $[93] !== t25 || $[94] !== value) {
    t26 = <APISearchSelect value={value} onValueChange={t13} remote={t16} formatOption={formatOption}>{t17}{t25}</APISearchSelect>;
    $[89] = formatOption;
    $[90] = t13;
    $[91] = t16;
    $[92] = t17;
    $[93] = t25;
    $[94] = value;
    $[95] = t26;
  } else {
    t26 = $[95];
  }
  let t27;
  if ($[96] !== t12 || $[97] !== t26) {
    t27 = <div className="space-y-1">{t12}{t26}</div>;
    $[96] = t12;
    $[97] = t26;
    $[98] = t27;
  } else {
    t27 = $[98];
  }
  return t27;
}

/** Explicit events write the form. Mounting this variant never rewrites historical splits. */
function _temp2(lot) {
  return lot.id;
}
function _temp(state) {
  return state.currentTenant;
}
export function DoseSingleLotPicker(t0) {
  const $ = _c(40);
  const {
    index,
    inventory
  } = t0;
  const {
    control,
    setValue,
    formState: t1
  } = useFormContext();
  const {
    errors
  } = t1;
  const t2 = `components.${index}`;
  let t3;
  if ($[0] !== control || $[1] !== t2) {
    t3 = {
      control,
      name: t2
    };
    $[0] = control;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const component = useWatch(t3);
  const {
    allLocationNameById
  } = useLocations();
  const tenant = useTenantStore(_temp3);
  const quantityId = useId();
  const [search, setSearch] = useState("");
  const medicationId = inventory.selectedMedication?.id ?? component.medicationId;
  const showDepleted = !!medicationId && component.includeDepletedMedicationId === medicationId;
  let t4;
  if ($[3] !== component || $[4] !== inventory) {
    t4 = getDoseLotCapabilities(component, inventory);
    $[3] = component;
    $[4] = inventory;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const {
    action
  } = t4;
  const draws = action === DoseLotSelectionAction.DRAW;
  const records = action === DoseLotSelectionAction.RECORD;
  const displayed = getDoseDisplayedAllocations(component, inventory);
  const receiptIds = getDoseReceiptIds(component);
  const references = parseDoseLotReferences(component.lotSelection?.value);
  const recorded = getRecordedDoseLots(component);
  let t5;
  if ($[6] !== inventory.isCheckingInventory || $[7] !== inventory.locationId || $[8] !== medicationId) {
    t5 = {
      medicationId,
      locationId: inventory.locationId,
      isLoadingInventory: inventory.isCheckingInventory
    };
    $[6] = inventory.isCheckingInventory;
    $[7] = inventory.locationId;
    $[8] = medicationId;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const historyQuery = useDoseLotHistory(t5);
  const selectedIds = Array.from(new Set([...(draws && component.lotAllocations !== undefined ? [] : receiptIds), ...displayed.map(_temp4)]));
  const unresolved = (draws || records) && selectedIds.some(receiptId => !inventory.shipments.some(lot_0 => lot_0.id === receiptId)) || draws && displayed.some(allocation => {
    const stock = inventory.preview?.stocks.find(entry => entry.stockId === allocation.stockId);
    return !stock?.unit || !Number.isFinite(stock.perAdministration) || stock.perAdministration <= 0 || inventory.shipments.find(lot => lot.id === allocation.shipmentId)?.stockId !== allocation.stockId;
  });
  const disabled = inventory.isCheckingInventory || !!inventory.previewError || unresolved;
  const stockIds = draws && component.correctActualInventoryUse ? (inventory.preview?.stocks ?? []).map(_temp5) : draws || records ? [""] : [];
  const savePlan = (next, consentReceiptId) => {
    if (!draws || disabled || !inventory.preview) {
      return;
    }
    const quotas = inferDoseStockAdministrationQuotas(next, next.length === 1 && !component.correctActualInventoryUse ? inventory.preview.stocks.filter(stock_1 => stock_1.stockId === next[0].stockId) : inventory.preview.stocks, component.quantity);
    const previousKey = doseAllocationDecisionKey(component, inventory);
    const key = doseAllocationDecisionKey({
      ...component,
      lotAllocations: next,
      stockAdministrationQuotas: quotas
    }, inventory);
    const committed = next.map(row_0 => {
      const previous = displayed.find(entry_0 => entry_0.shipmentId === row_0.shipmentId);
      const keepConsent = previous?.allowExtraYield && previous.quantity === row_0.quantity && previous.decisionKey === JSON.stringify([previousKey, previous.quantity]);
      return {
        shipmentId: row_0.shipmentId,
        stockId: row_0.stockId,
        quantity: row_0.quantity,
        ...((row_0.shipmentId === consentReceiptId || row_0.allowExtraYield && keepConsent) && {
          allowExtraYield: true,
          decisionKey: JSON.stringify([key, row_0.quantity])
        })
      };
    });
    setValue(`components.${index}.singleLotSelection`, true, {
      shouldDirty: true
    });
    setValue(`components.${index}.lotAllocationBasis`, undefined, {
      shouldDirty: true
    });
    setValue(`components.${index}.lotAllocations`, committed, {
      shouldDirty: true,
      shouldValidate: true
    });
    setValue(`components.${index}.stockAdministrationQuotas`, quotas, {
      shouldDirty: true,
      shouldValidate: true
    });
    setValue(`components.${index}.checkoutReuse`, false, {
      shouldDirty: true
    });
  };
  const select = lot_1 => {
    if (disabled) {
      return;
    }
    if (draws) {
      if (!getDosePickerShipments(component, {
        ...inventory,
        shipments: [lot_1]
      }).some(entry_1 => entry_1.id === lot_1.id)) {
        return;
      }
      const stock_2 = inventory.preview?.stocks.find(entry_2 => entry_2.stockId === lot_1.stockId);
      if (!stock_2) {
        return;
      }
      const previous_0 = displayed.filter(row_1 => row_1.stockId === lot_1.stockId);
      if (displayed.length === 1 && previous_0[0]?.shipmentId === lot_1.id) {
        return;
      }
      const quantity = previous_0.length ? sumAdministrationCounts(previous_0.map(_temp6)) : roundPhysicalQuantity(stock_2.perAdministration * component.quantity);
      savePlan([...(component.correctActualInventoryUse ? displayed.filter(row_3 => row_3.stockId !== lot_1.stockId) : []), {
        shipmentId: lot_1.id,
        stockId: lot_1.stockId,
        quantity
      }]);
    } else {
      if (records) {
        setValue(`components.${index}.clinicalShipmentIds`, [lot_1.id], {
          shouldDirty: true,
          shouldValidate: true
        });
      }
    }
  };
  const remove = receiptId_0 => {
    if (disabled) {
      return;
    }
    if (draws) {
      savePlan(displayed.filter(row_4 => row_4.shipmentId !== receiptId_0));
    } else {
      if (records) {
        setValue(`components.${index}.clinicalShipmentIds`, receiptIds.filter(entry_3 => entry_3 !== receiptId_0), {
          shouldDirty: true,
          shouldValidate: true
        });
      }
    }
  };
  const renderReceipt = (receiptId_1, t6) => {
    const presentation = t6 === undefined ? DoseReceiptPresentation.FULL : t6;
    const lot_2 = inventory.shipments.find(entry_4 => entry_4.id === receiptId_1);
    const allocation_0 = displayed.find(row_5 => row_5.shipmentId === receiptId_1);
    const stock_3 = inventory.preview?.stocks.find(entry_5 => entry_5.stockId === allocation_0?.stockId);
    const label = lot_2?.lotNumber || recorded.find(entry_6 => entry_6.shipmentId === receiptId_1)?.lotNumber || "Missing or unlabeled lot";
    const capacity = lot_2 ? doseLotCapacity(lot_2, inventory) : undefined;
    const needsExtraYield = allocation_0 && capacity && roundPhysicalQuantity(allocation_0.quantity - capacity.capacity) > 0;
    const consentCurrent = allocation_0?.allowExtraYield === true && allocation_0.decisionKey === JSON.stringify([doseAllocationDecisionKey(component, inventory), allocation_0.quantity]);
    if (presentation === DoseReceiptPresentation.FEEDBACK && (!draws || component.checkoutReuse || !needsExtraYield && !allocation_0?.allowExtraYield)) {
      return null;
    }
    return <div key={receiptId_1} className={presentation === DoseReceiptPresentation.FULL ? "space-y-1 border-b border-border py-2 last:border-b-0" : presentation === DoseReceiptPresentation.QUANTITY ? "w-24 shrink-0" : "space-y-1"}>{presentation === DoseReceiptPresentation.FULL && (lot_2 ? <LotShipmentDetails shipment={lot_2} productName={component.name} locationNames={allLocationNameById} /> : <LotRowLabel lotNumber={label} productName={component.name} wrap={true} />)}{presentation !== DoseReceiptPresentation.FEEDBACK && allocation_0 && <div className="block space-y-1 text-caption"><Label htmlFor={`${quantityId}-${receiptId_1}`}>{presentation === DoseReceiptPresentation.QUANTITY ? `Use${stock_3?.unit ? ` (${formatUnit(stock_3.unit)})` : ""}` : `Amount used from ${label}${stock_3?.unit ? ` (${formatUnit(stock_3.unit)})` : ""}`}</Label>{draws && !component.checkoutReuse ? <Input id={`${quantityId}-${receiptId_1}`} type="number" min={0} step="any" aria-label={`Amount used from ${label}`} value={Number.isFinite(allocation_0.quantity) ? allocation_0.quantity : ""} disabled={disabled || !lot_2 || !stock_3 || displayed.filter(row_6 => row_6.stockId === allocation_0.stockId).length > 1} onChange={event => {
          const quantity_0 = event.target.value === "" ? NaN : Number(event.target.value);
          savePlan(displayed.map(row_7 => row_7.shipmentId === receiptId_1 ? {
            ...row_7,
            quantity: quantity_0,
            allowExtraYield: false,
            decisionKey: undefined
          } : row_7));
        }} /> : <span className="block text-foreground-secondary">{`${formatQuantity(component.checkoutReuse ? lot_2?.chargedTodayQuantity ?? 0 : allocation_0.quantity)} ${stock_3?.unit ? formatUnit(stock_3.unit) : ""}${component.checkoutReuse ? " already charged at checkout (not resized)" : ""}`}</span>}</div>}{presentation !== DoseReceiptPresentation.QUANTITY && draws && needsExtraYield && !component.checkoutReuse && (capacity?.allowExtraYield ? <label className="flex items-start gap-2 text-caption"><Checkbox aria-label={`Use extra yield from ${label}. This exceeds available inventory.`} checked={consentCurrent} disabled={disabled} onCheckedChange={checked => savePlan(displayed.map(row_8 => row_8.shipmentId === receiptId_1 ? {
          ...row_8,
          allowExtraYield: checked === true
        } : row_8), checked === true ? receiptId_1 : undefined)} /><span>{`Use extra yield from ${label}`}</span></label> : <p role="alert" className="text-caption text-status-warning-text">This lot does not have enough available inventory. Choose another lot.</p>)}{presentation !== DoseReceiptPresentation.QUANTITY && draws && allocation_0?.allowExtraYield && !needsExtraYield && !component.checkoutReuse && <Button type="button" variant="ghost" size="sm" disabled={disabled} onClick={() => savePlan(displayed.map(row_9 => row_9.shipmentId === receiptId_1 ? {
        ...row_9,
        allowExtraYield: false,
        decisionKey: undefined
      } : row_9))}>Clear previous extra-yield approval</Button>}{presentation === DoseReceiptPresentation.FULL && (draws || records) && <Button type="button" variant="ghost" size="sm" disabled={disabled} onClick={() => remove(receiptId_1)}>Remove lot {label}</Button>}</div>;
  };
  const receiptStockId = receiptId_2 => inventory.shipments.find(lot_3 => lot_3.id === receiptId_2)?.stockId ?? displayed.find(row_10 => row_10.shipmentId === receiptId_2)?.stockId;
  const error = errors.components?.[index]?.lotSelection?.message;
  const t7 = "space-y-3";
  const t8 = stockIds.map(stockId => {
    const coverage = getDoseStockCoverage(displayed, inventory.preview?.stocks ?? [], component.quantity).find(entry_7 => entry_7.stockId === (stockId || displayed[0]?.stockId));
    const selected = stockId ? selectedIds.filter(receiptId_3 => receiptStockId(receiptId_3) === stockId) : selectedIds;
    const selectedLot = inventory.shipments.find(lot_4 => selected.includes(lot_4.id));
    const compact = selected.length === 1 && !!selectedLot && !unresolved && !component.checkoutReuse;
    return <div key={JSON.stringify([tenant, component.medicationId, component.id, inventory.locationId, inventory.providerId, stockId])} className="space-y-1"><div className="flex items-end gap-2" data-dose-inline-stock-row={true}><div className="min-w-0 flex-1"><DoseInlineLotSelect component={component} inventory={inventory} stockId={stockId || undefined} label={stockId ? `Lot number · ${inventory.selectedMedication?.stocks?.find(entry_8 => entry_8.stockId === stockId)?.stock.name ?? stockId}` : "Lot number"} value={selectedLot} disabled={disabled} showDepleted={showDepleted} onShowDepletedChange={show => setValue(`components.${index}.includeDepletedMedicationId`, show ? medicationId : undefined, {
            shouldDirty: true
          })} onSelect={select} onClear={() => {
            if (disabled) {
              return;
            }
            if (draws && !component.id && !inventory.preview?.ownedLots.length) {
              setValue(`components.${index}.lotAllocations`, undefined, {
                shouldDirty: true,
                shouldValidate: true
              });
              setValue(`components.${index}.stockAdministrationQuotas`, undefined, {
                shouldDirty: true
              });
              setValue(`components.${index}.lotAllocationBasis`, undefined, {
                shouldDirty: true
              });
              setValue(`components.${index}.checkoutReuse`, false, {
                shouldDirty: true
              });
            } else {
              if (draws) {
                savePlan(stockId ? displayed.filter(row_11 => row_11.stockId !== stockId) : []);
              } else {
                setValue(`components.${index}.clinicalShipmentIds`, [], {
                  shouldDirty: true,
                  shouldValidate: true
                });
              }
            }
          }} onSearchChange={setSearch} /></div>{compact && displayed.some(row_12 => row_12.shipmentId === selected[0]) && renderReceipt(selected[0], DoseReceiptPresentation.QUANTITY)}</div>{compact ? renderReceipt(selected[0], DoseReceiptPresentation.FEEDBACK) : selected.map(receiptId_4 => renderReceipt(receiptId_4))}{draws && coverage && component.lotAllocations !== undefined && coverage.deltaPhysical !== 0 && <p role="status" className="text-caption text-status-warning-text">{`${formatQuantity(coverage.allocatedPhysical)} of ${formatQuantity(coverage.requiredPhysical)} ${coverage.unit ? formatUnit(coverage.unit) : ""} assigned.`}</p>}{draws && selected.length > 1 && <p className="text-caption text-foreground-secondary">This dose has a historical split across lots. Choose one replacement lot for this stock before changing its amount.</p>}</div>;
  });
  const t9 = selectedIds.filter(receiptId_5 => !stockIds.some(stockId_0 => !stockId_0 || receiptStockId(receiptId_5) === stockId_0)).map(receiptId_6 => renderReceipt(receiptId_6));
  const t10 = references.map(code => <div key={code} className="flex items-start justify-between gap-2 border-b border-border py-2 last:border-b-0"><LotRowLabel lotNumber={code} productName={component.name} wrap={true} /><Button type="button" variant="ghost" size="sm" disabled={disabled} onClick={() => {
      const next_0 = references.filter(entry_9 => entry_9 !== code);
      setValue(`components.${index}.lotSelection`, next_0.length ? {
        type: "lot",
        value: next_0.join("\n")
      } : null, {
        shouldDirty: true,
        shouldValidate: true
      });
    }}>Remove lot {code}</Button></div>);
  let t11;
  if ($[10] !== index || $[11] !== inventory) {
    t11 = <DoseInventoryActions index={index} inventory={inventory} />;
    $[10] = index;
    $[11] = inventory;
    $[12] = t11;
  } else {
    t11 = $[12];
  }
  let t12;
  if ($[13] !== component.correctActualInventoryUse || $[14] !== component.name || $[15] !== disabled || $[16] !== historyQuery || $[17] !== index || $[18] !== inventory.shipments || $[19] !== medicationId || $[20] !== references || $[21] !== search || $[22] !== select || $[23] !== setValue) {
    t12 = !component.correctActualInventoryUse && <DoseLotReferenceOptions medicationId={medicationId} medicationName={component.name} shipments={inventory.shipments} search={search} references={references} onAdd={code_0 => {
      if (!disabled) {
        setValue(`components.${index}.lotSelection`, {
          type: "lot",
          value: [...references, code_0].join("\n")
        }, {
          shouldDirty: true,
          shouldValidate: true
        });
      }
    }} onSelectShipment={receiptId_7 => {
      const lot_5 = inventory.shipments.find(entry_10 => entry_10.id === receiptId_7);
      if (lot_5) {
        select(lot_5);
      }
    }} isLoadingInventory={disabled} onPendingChange={_temp7} historyQuery={historyQuery} />;
    $[13] = component.correctActualInventoryUse;
    $[14] = component.name;
    $[15] = disabled;
    $[16] = historyQuery;
    $[17] = index;
    $[18] = inventory.shipments;
    $[19] = medicationId;
    $[20] = references;
    $[21] = search;
    $[22] = select;
    $[23] = setValue;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== unresolved) {
    t13 = unresolved && <p role="alert" className="text-caption text-status-warning-text">Recorded inventory lots are unavailable. Refresh before changing their amounts.</p>;
    $[25] = unresolved;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== inventory.previewError) {
    t14 = inventory.previewError && <p role="alert" className="text-caption text-status-warning-text">Inventory requirements could not be verified. Refresh and try again.</p>;
    $[27] = inventory.previewError;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] !== error) {
    t15 = error && <p role="alert" className="text-caption text-status-warning-text">{String(error)}</p>;
    $[29] = error;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  let t16;
  if ($[31] !== t10 || $[32] !== t11 || $[33] !== t12 || $[34] !== t13 || $[35] !== t14 || $[36] !== t15 || $[37] !== t8 || $[38] !== t9) {
    t16 = <div className={t7}>{t8}{t9}{t10}{t11}{t12}{t13}{t14}{t15}</div>;
    $[31] = t10;
    $[32] = t11;
    $[33] = t12;
    $[34] = t13;
    $[35] = t14;
    $[36] = t15;
    $[37] = t8;
    $[38] = t9;
    $[39] = t16;
  } else {
    t16 = $[39];
  }
  return t16;
}
function _temp7() {}
function _temp6(row_2) {
  return row_2.quantity;
}
function _temp5(stock_0) {
  return stock_0.stockId;
}
function _temp4(row) {
  return row.shipmentId;
}
function _temp3(state) {
  return state.currentTenant;
}